import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, TextField, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Grid, Divider, Tooltip, IconButton, Checkbox, FormControlLabel
} from '@mui/material';
import { Campaign, Assignment, PersonAdd, CheckCircle, Add, Refresh, Key, Facebook, Instagram, DeleteForever, PlayArrow, Pause, Edit, Delete } from '@mui/icons-material';
import { metaAdsAPI } from '../services/api';
import FacebookConnectButton from './FacebookConnectButton';

export default function MetaAdsManager({ onNotify, showHeaderOnly = false, showContentOnly = false }) {
  const [appStatus, setAppStatus] = useState(null);

  const [statusLoading, setStatusLoading] = useState(false);

  // Meta Connected Account state
  const [metaAccount, setMetaAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);

  // Sub-tabs: 0: Campaigns, 1: Lead Forms, 2: Captured Leads
  const [activeSubTab, setActiveSubTab] = useState(0);

  // Data states
  const [campaigns, setCampaigns] = useState([]);
  const [leadForms, setLeadForms] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters / Custom Configs
  const [adAccountId, setAdAccountId] = useState('');
  const [pageId, setPageId] = useState('');
  const [customToken, setCustomToken] = useState('');

  // Dialogs & Campaign Management States
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: '', objective: 'OUTCOME_LEADS', status: 'PAUSED', dailyBudget: 500 });
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const [editCampaignOpen, setEditCampaignOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(false);
  const [editCampaignData, setEditCampaignData] = useState({ id: '', name: '', dailyBudget: 500, status: 'PAUSED' });

  const [deleteCampaignOpen, setDeleteCampaignOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  const [togglingCampaignId, setTogglingCampaignId] = useState(null);

  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [formModalAlert, setFormModalAlert] = useState(null);
  const [newForm, setNewForm] = useState({
    name: '',
    privacyPolicyUrl: 'https://fitistic.yuviso.com/privacy',
    followUpUrl: 'https://fitistic.yuviso.com',
    includeGoalQuestion: true,
    includeTimeQuestion: true,
    includeTrialOffer: true,
  });
  const [creatingForm, setCreatingForm] = useState(false);

  const [convertingLeadId, setConvertingLeadId] = useState(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    checkAccountStatus();
    fetchConnectedMetaAccount();
  }, []);

  useEffect(() => {
    if (activeSubTab === 0) fetchCampaigns();
    else if (activeSubTab === 1) fetchLeadForms();
    else if (activeSubTab === 2) fetchLeads();

    // Auto-refresh analytics and campaign data every 20 seconds
    const interval = setInterval(() => {
      if (activeSubTab === 0) fetchCampaigns();
      else if (activeSubTab === 1) fetchLeadForms();
      else if (activeSubTab === 2) fetchLeads();
    }, 20000);

    return () => clearInterval(interval);
  }, [activeSubTab, pageId]);

  const checkAccountStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await metaAdsAPI.getAccountStatus({ accessToken: customToken || undefined });
      setAppStatus(res.data);
    } catch (err) {
      setAppStatus({ success: false, error: err.response?.data?.message || 'Failed to connect to Meta API' });
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchConnectedMetaAccount = async () => {
    setAccountLoading(true);
    try {
      const res = await metaAdsAPI.getConnectedAccount();
      if (res.data.success && res.data.data) {
        setMetaAccount(res.data.data);
        // Auto-select first page if pageId is not set
        if (res.data.data.pages && res.data.data.pages.length > 0 && !pageId) {
          setPageId(res.data.data.pages[0].pageId);
        }
        // Auto-select first ad account if adAccountId is not set
        if (res.data.data.adAccounts && res.data.data.adAccounts.length > 0 && !adAccountId) {
          setAdAccountId(res.data.data.adAccounts[0].id);
        } else if (!adAccountId) {
          setAdAccountId('act_344920096234467');
        }
      } else {
        setMetaAccount(null);
      }
    } catch (err) {
      console.error('Error fetching connected Meta account:', err);
    } finally {
      setAccountLoading(false);
    }
  };

  const handleOAuthConnected = (accountData) => {
    setMetaAccount(accountData);
    if (accountData.pages && accountData.pages.length > 0) {
      setPageId(accountData.pages[0].pageId);
    }
    if (accountData.adAccounts && accountData.adAccounts.length > 0) {
      setAdAccountId(accountData.adAccounts[0].id);
    } else {
      setAdAccountId('act_344920096234467');
    }
    onNotify?.(`Successfully connected Meta account: ${accountData.userName}!`);
    fetchCampaigns();
    fetchLeadForms();
  };

  const handleDisconnect = () => {
    setDisconnectDialogOpen(true);
  };

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await metaAdsAPI.disconnectAccount();
      setMetaAccount(null);
      setPageId('');
      setAdAccountId('');
      setDisconnectDialogOpen(false);
      onNotify?.('Disconnected Meta Account');
    } catch (err) {
      onNotify?.('Failed to disconnect Meta account', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await metaAdsAPI.getCampaigns({ adAccountId: adAccountId || undefined, accessToken: customToken || undefined });
      if (res.data.success) {
        setCampaigns(res.data.data);
      } else {
        onNotify?.(res.data.error || 'Failed to fetch campaigns', 'error');
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Error fetching campaigns', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) return;
    setCreatingCampaign(true);
    try {
      const res = await metaAdsAPI.createCampaign({
        adAccountId: adAccountId || undefined,
        accessToken: customToken || undefined,
        ...newCampaign,
      });
      if (res.data.success) {
        onNotify?.('Campaign created successfully on Meta!');
        setCreateCampaignOpen(false);
        setNewCampaign({ name: '', objective: 'OUTREACH', status: 'PAUSED', dailyBudget: 500 });
        fetchCampaigns();
      } else {
        onNotify?.(res.data.error || 'Failed to create campaign', 'error');
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Failed to create campaign', 'error');
    } finally {
      setCreatingCampaign(false);
    }
  };

  // Toggle Pause / Active status for a campaign
  const handleToggleCampaignStatus = async (campaignId, currentStatus) => {
    const nextStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setTogglingCampaignId(campaignId);
    try {
      const res = await metaAdsAPI.updateCampaign(campaignId, {
        status: nextStatus,
        adAccountId: adAccountId || undefined,
        accessToken: customToken || undefined,
      });
      if (res.data.success) {
        onNotify?.(`Campaign ${nextStatus === 'ACTIVE' ? 'activated' : 'paused'} successfully!`);
        fetchCampaigns();
      } else {
        onNotify?.(res.data.error || `Failed to ${nextStatus === 'ACTIVE' ? 'activate' : 'pause'} campaign`, 'error');
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Error updating campaign status', 'error');
    } finally {
      setTogglingCampaignId(null);
    }
  };

  // Open Edit Dialog
  const handleOpenEditCampaign = (campaign) => {
    setEditCampaignData({
      id: campaign.id,
      name: campaign.name || '',
      dailyBudget: campaign.daily_budget ? Math.round(campaign.daily_budget / 100) : 500,
      status: campaign.status || 'PAUSED',
    });
    setEditCampaignOpen(true);
  };

  // Save Edit Campaign
  const handleSaveEditCampaign = async () => {
    if (!editCampaignData.id || !editCampaignData.name.trim()) return;
    setEditingCampaign(true);
    try {
      const res = await metaAdsAPI.updateCampaign(editCampaignData.id, {
        name: editCampaignData.name,
        dailyBudget: editCampaignData.dailyBudget,
        status: editCampaignData.status,
        adAccountId: adAccountId || undefined,
        accessToken: customToken || undefined,
      });
      if (res.data.success) {
        onNotify?.('Campaign updated successfully on Meta!');
        setEditCampaignOpen(false);
        fetchCampaigns();
      } else {
        onNotify?.(res.data.error || 'Failed to update campaign', 'error');
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Error updating campaign', 'error');
    } finally {
      setEditingCampaign(false);
    }
  };

  // Open Delete Dialog
  const handleOpenDeleteCampaign = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteCampaignOpen(true);
  };

  // Confirm Delete / Archive Campaign
  const handleConfirmDeleteCampaign = async () => {
    if (!campaignToDelete?.id) return;
    setDeletingCampaign(true);
    try {
      const res = await metaAdsAPI.deleteCampaign(campaignToDelete.id, {
        adAccountId: adAccountId || undefined,
        accessToken: customToken || undefined,
      });
      if (res.data.success) {
        onNotify?.('Campaign deleted / archived successfully on Meta!');
        setDeleteCampaignOpen(false);
        setCampaignToDelete(null);
        fetchCampaigns();
      } else {
        onNotify?.(res.data.error || 'Failed to delete campaign', 'error');
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Error deleting campaign', 'error');
    } finally {
      setDeletingCampaign(false);
    }
  };

  const fetchLeadForms = async () => {
    if (!pageId) {
      setLeadForms([]);
      return;
    }
    setLoading(true);
    try {
      const res = await metaAdsAPI.getLeadForms({ pageId, accessToken: customToken || undefined });
      if (res.data.success) {
        setLeadForms(res.data.data);
      } else {
        onNotify?.(res.data.error || 'Failed to fetch lead forms', 'error');
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Error fetching lead forms', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateForm = () => {
    setFormModalAlert(null);
    setCreateFormOpen(true);
  };

  const handleCreateForm = async () => {
    setFormModalAlert(null);
    if (!pageId) {
      setFormModalAlert({ type: 'warning', msg: 'Please select a Facebook Page above before creating a lead form.' });
      return;
    }
    if (!newForm.name.trim()) {
      setFormModalAlert({ type: 'warning', msg: 'Please enter a Form Name.' });
      return;
    }
    setCreatingForm(true);
    try {
      const res = await metaAdsAPI.createLeadForm({
        pageId,
        accessToken: customToken || undefined,
        ...newForm,
      });
      if (res.data.success) {
        setFormModalAlert({ type: 'success', msg: 'Lead form created successfully on Meta!' });
        setTimeout(() => {
          setCreateFormOpen(false);
          setFormModalAlert(null);
          setNewForm({
            name: '',
            privacyPolicyUrl: 'https://fitistic.yuviso.com/privacy',
            followUpUrl: 'https://fitistic.yuviso.com',
            includeGoalQuestion: true,
            includeTimeQuestion: true,
            includeTrialOffer: true,
          });
          fetchLeadForms();
        }, 1500);
      } else {
        setFormModalAlert({ type: 'error', msg: res.data.error || 'Failed to create lead form on Meta' });
      }
    } catch (err) {
      setFormModalAlert({ type: 'error', msg: err.response?.data?.message || 'Failed to create lead form' });
    } finally {
      setCreatingForm(false);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await metaAdsAPI.getAllStoredLeads();
      if (res.data.success) {
        setLeads(res.data.data);
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Error fetching stored leads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertLead = async (leadId) => {
    setConvertingLeadId(leadId);
    try {
      const res = await metaAdsAPI.convertLeadToMember(leadId);
      if (res.data.success) {
        onNotify?.('Lead successfully converted to member!');
        fetchLeads();
      }
    } catch (err) {
      onNotify?.(err.response?.data?.message || 'Failed to convert lead', 'error');
    } finally {
      setConvertingLeadId(null);
    }
  };

  // Header view component (OAuth Card + Dropdowns + Token Status + Disconnect Dialog)
  const renderHeaderUI = () => (
    <Box sx={{ mt: 1 }}>
      {/* Meta OAuth Connection Card */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, background: metaAccount ? '#f0fdf4' : '#f8fafc', borderColor: metaAccount ? '#bbf7d0' : '#e2e8f0' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Facebook sx={{ color: '#1877F2', fontSize: 32 }} />
                <Typography variant="h6" fontWeight="bold">
                  {metaAccount ? `Connected Meta Account: ${metaAccount.userName}` : 'Connect Meta Account'}
                </Typography>
                <Chip
                  label={metaAccount ? 'Connected' : 'Not Connected'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: metaAccount ? '#f0fdf4' : '#f8fafc',
                    color: metaAccount ? '#15803d' : '#64748b',
                    border: '1px solid',
                    borderColor: metaAccount ? '#bbf7d0' : '#e2e8f0',
                  }}
                />
              </Stack>

              {metaAccount ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Meta User ID: <strong>{metaAccount.facebookUserId}</strong> • Connected Pages: <strong>{metaAccount.pages?.length || 0}</strong>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Log in with Meta to automatically discover Facebook Pages, linked Instagram Business Accounts, and manage Lead Forms.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={5} textAlign={{ xs: 'left', md: 'right' }}>
              {metaAccount ? (
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteForever />}
                    onClick={handleDisconnect}
                  >
                    Disconnect Account
                  </Button>
                </Stack>
              ) : (
                <FacebookConnectButton
                  onConnected={handleOAuthConnected}
                  onError={(err) => onNotify?.(err, 'error')}
                />
              )}
            </Grid>
          </Grid>

          {/* Connected Meta Pages & Instagram Accounts List */}
          {metaAccount && metaAccount.pages && metaAccount.pages.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #dcfce7' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>
                Connected Meta Pages & Linked Instagram Accounts:
              </Typography>
              <Grid container spacing={1.5}>
                {metaAccount.pages.map((p) => (
                  <Grid item xs={12} sm={6} md={4} key={p.pageId}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bg: '#fff' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Facebook fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {p.pageName}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Page ID: {p.pageId}
                      </Typography>

                      {p.instagramAccountId ? (
                        <Chip
                          icon={<Instagram fontSize="small" style={{ color: '#E4405F' }} />}
                          label={p.instagramUsername ? `@${p.instagramUsername}` : `IG: ${p.instagramAccountId}`}
                          size="small"
                          sx={{ mt: 1, backgroundColor: '#fce7f3', color: '#be185d', fontWeight: 'bold' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          No linked Instagram Business Account
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Control Bar: Dropdowns & Status Badge (Only shown when connected) */}
      {metaAccount && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* 1. Meta Page Selector Dropdown */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!metaAccount || !metaAccount.pages?.length}>
                <InputLabel>Meta Page</InputLabel>
                <Select
                  value={pageId}
                  label="Meta Page"
                  onChange={(e) => setPageId(e.target.value)}
                >
                  {metaAccount && metaAccount.pages && metaAccount.pages.length > 0 ? (
                    metaAccount.pages.map((p) => (
                      <MenuItem key={p.pageId} value={p.pageId}>
                        {p.pageName} ({p.pageId})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No Meta Pages Connected</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* 2. Meta Ad Account Selector Dropdown */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Meta Ad Account</InputLabel>
                <Select
                  value={adAccountId || 'act_344920096234467'}
                  label="Meta Ad Account"
                  onChange={(e) => setAdAccountId(e.target.value)}
                >
                  {metaAccount && metaAccount.adAccounts && metaAccount.adAccounts.length > 0 ? (
                    metaAccount.adAccounts.map((acc) => (
                      <MenuItem key={acc.id} value={acc.id}>
                        {acc.name} ({acc.id})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="act_344920096234467">
                      LearnBits Ad Account (act_344920096234467)
                    </MenuItem>
                  )}
                  {metaAccount?.adAccounts?.length > 0 &&
                    !metaAccount.adAccounts.some((a) => a.id.includes('344920096234467')) && (
                      <MenuItem value="act_344920096234467">
                        LearnBits Ad Account (act_344920096234467)
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
            </Grid>

            {/* 3. Authentication Status Indicator */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1, textAlign: 'center', backgroundColor: '#f0fdf4' }}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold">
                  OAuth Token Status
                </Typography>
                <Chip
                  icon={<CheckCircle sx={{ color: '#15803d !important' }} />}
                  label="Connected (60-Day Token)"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    bgcolor: '#f0fdf4',
                    color: '#15803d',
                    border: '1px solid #bbf7d0',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}


      {/* Disconnect Dialog */}
      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)}>
        <DialogTitle>Disconnect Meta Account?</DialogTitle>
        <DialogContent>
          <Typography>
            This will disconnect your Meta user account and Facebook Pages from this gym. You can reconnect anytime.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDisconnect} disabled={disconnecting}>
            {disconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (showHeaderOnly) return renderHeaderUI();

  return (
    <Box sx={{ mt: 1 }}>
      {!showContentOnly && renderHeaderUI()}
      {/* Nav Buttons for Sub-Tabs */}

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant={activeSubTab === 0 ? 'contained' : 'outlined'}
            startIcon={<Campaign />}
            onClick={() => setActiveSubTab(0)}
          >
            Meta Campaigns
          </Button>

        <Button
          variant={activeSubTab === 1 ? 'contained' : 'outlined'}
          startIcon={<Assignment />}
          onClick={() => setActiveSubTab(1)}
        >
          Lead Generation Forms
        </Button>
        <Button
          variant={activeSubTab === 2 ? 'contained' : 'outlined'}
          startIcon={<PersonAdd />}
          onClick={() => setActiveSubTab(2)}
        >
          Captured Lead Submissions
        </Button>
      </Stack>

      {/* ── Sub-Tab 0: Campaigns ────────────────────────────────────────────── */}
      {activeSubTab === 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Meta Ad Campaigns
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={fetchCampaigns}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={() => setCreateCampaignOpen(true)}>
                  Create Campaign
                </Button>
              </Stack>
            </Stack>

            {loading ? (
              <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : campaigns.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                No active Meta Ad Campaigns found. Click <strong>Create Campaign</strong> to launch a new campaign.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f1f5f9' }}>
                      <TableCell><strong>Campaign ID</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Objective</strong></TableCell>
                      <TableCell><strong>Daily Budget</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Created Date</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell><Typography variant="body2" fontFamily="monospace">{c.id}</Typography></TableCell>
                        <TableCell><strong>{c.name}</strong></TableCell>
                        <TableCell><Chip label={c.objective} size="small" color="secondary" variant="outlined" /></TableCell>
                        <TableCell>{c.daily_budget ? `₹${(c.daily_budget / 100).toLocaleString()}` : 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={c.status}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: c.status === 'ACTIVE' ? '#f0fdf4' : '#f8fafc',
                              color: c.status === 'ACTIVE' ? '#15803d' : '#64748b',
                              border: '1px solid',
                              borderColor: c.status === 'ACTIVE' ? '#bbf7d0' : '#e2e8f0',
                            }}
                          />
                        </TableCell>
                        <TableCell>{c.created_time ? new Date(c.created_time).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={c.status === 'ACTIVE' ? 'Pause Campaign' : 'Activate Campaign'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color={c.status === 'ACTIVE' ? 'warning' : 'success'}
                                  onClick={() => handleToggleCampaignStatus(c.id, c.status)}
                                  disabled={togglingCampaignId === c.id}
                                >
                                  {togglingCampaignId === c.id ? <CircularProgress size={16} color="inherit" /> : c.status === 'ACTIVE' ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit Campaign (Budget & Name)">
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditCampaign(c)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete / Archive Campaign">
                              <IconButton size="small" color="error" onClick={() => handleOpenDeleteCampaign(c)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Sub-Tab 1: Lead Forms ───────────────────────────────────────────── */}
      {activeSubTab === 1 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Meta Lead Generation Forms
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={fetchLeadForms} disabled={!pageId}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateForm}>
                  Create Lead Form
                </Button>
              </Stack>
            </Stack>

            {!pageId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select or enter a <strong>Facebook Page</strong> above to query or create Lead Forms for your page.
              </Alert>
            )}

            {loading ? (
              <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : leadForms.length === 0 ? (
              <Alert severity="info">
                No Lead Forms found for this Page. Click <strong>Create Lead Form</strong> to set up a new lead form on Facebook/Instagram.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f1f5f9' }}>
                      <TableCell><strong>Form ID</strong></TableCell>
                      <TableCell><strong>Form Name</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Leads Count</strong></TableCell>
                      <TableCell><strong>Created Date</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadForms.map((f) => (
                      <TableRow key={f.id} hover>
                        <TableCell><Typography variant="body2" fontFamily="monospace">{f.id}</Typography></TableCell>
                        <TableCell><strong>{f.name}</strong></TableCell>
                        <TableCell>
                          <Chip
                            label={f.status || 'ACTIVE'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: (f.status || 'ACTIVE') === 'ACTIVE' ? '#f0fdf4' : '#f8fafc',
                              color: (f.status || 'ACTIVE') === 'ACTIVE' ? '#15803d' : '#64748b',
                              border: '1px solid',
                              borderColor: (f.status || 'ACTIVE') === 'ACTIVE' ? '#bbf7d0' : '#e2e8f0',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${f.leads_count || 0} Leads`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #bfdbfe',
                            }}
                          />
                        </TableCell>
                        <TableCell>{f.created_time ? new Date(f.created_time).toLocaleDateString() : 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Sub-Tab 2: Captured Leads ────────────────────────────────────────── */}
      {activeSubTab === 2 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Captured Meta Leads
              </Typography>
              <Button variant="outlined" startIcon={<Refresh />} onClick={fetchLeads}>
                Refresh Leads
              </Button>
            </Stack>

            {loading ? (
              <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : leads.length === 0 ? (
              <Alert severity="info">
                No Meta leads synced yet. Leads generated via your Facebook/Instagram Lead Ads will appear here.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f1f5f9' }}>
                      <TableCell><strong>Full Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Form Name</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Captured Date</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leads.map((l) => (
                      <TableRow key={l._id} hover>
                        <TableCell><strong>{l.fullName}</strong></TableCell>
                        <TableCell>{l.email || 'N/A'}</TableCell>
                        <TableCell>{l.phone || 'N/A'}</TableCell>
                        <TableCell>{l.formName}</TableCell>
                        <TableCell>
                          <Chip
                            label={(l.status || 'new').toUpperCase()}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: l.status === 'converted' ? '#f0fdf4' : '#fffbeb',
                              color: l.status === 'converted' ? '#15803d' : '#b45309',
                              border: '1px solid',
                              borderColor: l.status === 'converted' ? '#bbf7d0' : '#fde68a',
                            }}
                          />
                        </TableCell>
                        <TableCell>{new Date(l.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          {l.status === 'converted' ? (
                            <Chip
                              icon={<CheckCircle sx={{ color: '#15803d !important' }} />}
                              label="Member Created"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: '#f0fdf4',
                                color: '#15803d',
                                border: '1px solid #bbf7d0',
                              }}
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={convertingLeadId === l._id ? <CircularProgress size={14} color="inherit" /> : <PersonAdd />}
                              onClick={() => handleConvertLead(l._id)}
                              disabled={convertingLeadId === l._id}
                            >
                              Convert to Member
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Dialog: Create Campaign ────────────────────────────────────────── */}
      <Dialog open={createCampaignOpen} onClose={() => setCreateCampaignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Meta Ad Campaign</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!adAccountId && (!metaAccount?.adAccounts || metaAccount.adAccounts.length === 0) && (
              <Alert severity="warning">
                No Meta Ad Account found. You can create Facebook Lead Forms for free under the <strong>Lead Generation Forms</strong> tab! To run paid ad campaigns, set up an Ad Account in Meta Ads Manager.
              </Alert>
            )}
            <TextField
              fullWidth
              label="Campaign Name"
              value={newCampaign.name}
              onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Objective</InputLabel>
              <Select
                value={newCampaign.objective}
                label="Objective"
                onChange={(e) => setNewCampaign({ ...newCampaign, objective: e.target.value })}
              >
                <MenuItem value="OUTCOME_LEADS">Lead Generation (Meta Forms)</MenuItem>
                <MenuItem value="OUTCOME_AWARENESS">Brand Awareness / Reach</MenuItem>
                <MenuItem value="OUTCOME_ENGAGEMENT">Direct Messages (WhatsApp / Messenger)</MenuItem>
                <MenuItem value="OUTCOME_SALES">Sales & Conversions</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="number"
              label="Daily Budget (₹)"
              value={newCampaign.dailyBudget}
              onChange={(e) => setNewCampaign({ ...newCampaign, dailyBudget: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Initial Status</InputLabel>
              <Select
                value={newCampaign.status}
                label="Initial Status"
                onChange={(e) => setNewCampaign({ ...newCampaign, status: e.target.value })}
              >
                <MenuItem value="PAUSED">Paused (Recommended)</MenuItem>
                <MenuItem value="ACTIVE">Active immediately</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCampaignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCampaign}
            disabled={creatingCampaign || !newCampaign.name.trim()}
          >
            {creatingCampaign ? <CircularProgress size={20} /> : 'Create Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Edit Campaign ─────────────────────────────────────────── */}
      <Dialog open={editCampaignOpen} onClose={() => setEditCampaignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Meta Ad Campaign</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Campaign Name"
              value={editCampaignData.name}
              onChange={(e) => setEditCampaignData({ ...editCampaignData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              type="number"
              label="Daily Budget (₹)"
              value={editCampaignData.dailyBudget}
              onChange={(e) => setEditCampaignData({ ...editCampaignData, dailyBudget: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Campaign Status</InputLabel>
              <Select
                value={editCampaignData.status}
                label="Campaign Status"
                onChange={(e) => setEditCampaignData({ ...editCampaignData, status: e.target.value })}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="PAUSED">PAUSED</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCampaignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditCampaign}
            disabled={editingCampaign || !editCampaignData.name.trim()}
          >
            {editingCampaign ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Delete Campaign ───────────────────────────────── */}
      <Dialog open={deleteCampaignOpen} onClose={() => setDeleteCampaignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
          Delete / Archive Campaign
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete/archive campaign <strong>{campaignToDelete?.name}</strong> on Meta?
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will set the campaign status to DELETED on Meta Ads Manager. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteCampaignOpen(false)} disabled={deletingCampaign}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteCampaign}
            disabled={deletingCampaign}
            startIcon={deletingCampaign ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
          >
            {deletingCampaign ? 'Deleting...' : 'Delete Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Create Lead Form ───────────────────────────────────────── */}
      <Dialog open={createFormOpen} onClose={() => { setCreateFormOpen(false); setFormModalAlert(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Create Meta Lead Generation Form</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formModalAlert && (
              <Alert severity={formModalAlert.type} onClose={() => setFormModalAlert(null)}>
                {formModalAlert.msg}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Form Name"
              value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="e.g. Free 7-Day Gym Trial Lead Form"
              required
            />
            <TextField
              fullWidth
              label="Privacy Policy URL"
              value={newForm.privacyPolicyUrl}
              onChange={(e) => setNewForm({ ...newForm, privacyPolicyUrl: e.target.value })}
            />
            <TextField
              fullWidth
              label="Thank You / Follow-up URL"
              value={newForm.followUpUrl}
              onChange={(e) => setNewForm({ ...newForm, followUpUrl: e.target.value })}
            />
            <Alert severity="info">
              Default questions included: <strong>Full Name, Email Address, and Phone Number</strong>.
            </Alert>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" fontWeight="bold">
              Gym Lead Capture Options (Custom Questions):
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newForm.includeGoalQuestion}
                  onChange={(e) => setNewForm({ ...newForm, includeGoalQuestion: e.target.checked })}
                />
              }
              label="Ask Primary Fitness Goal (Weight Loss, Muscle Gain, Toning, Personal Training)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newForm.includeTimeQuestion}
                  onChange={(e) => setNewForm({ ...newForm, includeTimeQuestion: e.target.checked })}
                />
              }
              label="Ask Preferred Workout Time Slot (Morning, Afternoon, Evening)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newForm.includeTrialOffer}
                  onChange={(e) => setNewForm({ ...newForm, includeTrialOffer: e.target.checked })}
                />
              }
              label="Include Free Pass / Trial Claim Offer Selection (1-Day VIP Pass, Free Consult, 3-Day Pass)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateFormOpen(false); setFormModalAlert(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateForm}
            disabled={creatingForm || !newForm.name.trim() || !pageId}
          >
            {creatingForm ? <CircularProgress size={20} /> : 'Create Lead Form'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Disconnect Meta Account ───────────────────────── */}
      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
          Disconnect Meta Integration
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to disconnect your Facebook & Instagram account?
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will pause automated Meta lead form sync and campaign access for this gym. You can reconnect anytime.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDisconnectDialogOpen(false)} disabled={disconnecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDisconnect}
            disabled={disconnecting}
            startIcon={disconnecting ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
